import { renderTemplate, stageByNo, type JourneyContent } from '../../../entities/journey'

// Branching and choices: «Где я в расставании (1).html», eight-stage model.
export const questions = {
  end: { title: "Ваши отношения закончились?", options: ["Да, мы расстались", "Мы больше не пара, но общаемся", "Мы на паузе", "Нет, мы вместе"] },
  told: { title: "Партнёр знает, что вы недовольны?", options: ["Да, мы поговорили об этом прямо", "Догадывается по намёкам", "Нет", "Это партнёр сказал мне, что недоволен"] },
  weigh: { title: "Вы думаете о том, чтобы уйти?", options: ["Да", "Нет, но что-то не так", "Нет, меня всё устраивает"] },
  discuss: { title: "Вы с партнёром обсуждаете, что делать?", options: ["Да", "Нет, проблему только назвали", "Мы избегаем этой темы"] },
  trying: { title: "Вы уже пробуете конкретное решение?", options: ["Да", "Нет, пока только говорим"] },
  pauseTalk: { title: "До паузы вы обсуждали проблемы с партнёром?", options: ["Да, много раз", "Один раз", "Нет"] },
  env: { title: "Близкие знают о ваших проблемах?", options: ["Да, я с ними советуюсь", "Знают, но я это не обсуждаю", "Нет"] },
  partner: { title: "Как вам кажется, партнёр думает о расставании?", options: ["Больше, чем я", "Примерно как я", "Меньше, чем я", "Не знаю"] },
  cycle: { title: "Вы уже расходились и сходились снова?", options: ["Да", "Нет"] },
  now: { title: "О чём вы сейчас чаще думаете?", options: ["О том, что произошло и почему", "О будущем", "Ни о том, ни о другом: всё слишком свежо"] },
  story: { title: "Вы можете в двух словах объяснить, почему всё закончилось?", options: ["Да", "Пока нет"] },
  lessons: { title: "Вы знаете, чего хотите от следующих отношений?", options: ["Да", "Отчасти", "Пока нет"] },
  who: { title: "Кто решил расстаться?", options: ["Я", "Партнёр", "Мы вместе", "Само сошло на нет"] },
  surprise: { title: "Расставание было для вас неожиданным?", options: ["Да", "Отчасти", "Нет"] },
  talk: { title: "До расставания вы обсуждали проблемы с партнёром?", options: ["Да, много раз", "Один раз", "Нет"] },
  tried: { title: "До расставания вы пробовали что-то изменить?", options: ["Да", "Нет"] },
  envEnd: { title: "Близкие знали о проблемах до расставания?", options: ["Да, мы с ними это обсуждали", "Узнали только после", "Нет, и сейчас не знают"] },
  contact: { title: "Вы сейчас общаетесь с бывшим партнёром?", options: ["Нет", "Да, и понятно, кто мы друг другу", "Да, но непонятно, кто мы друг другу"] },
}
export type QuestionId = keyof typeof questions
export type Answer = { question: QuestionId; choice: number }
export type StageStatus = 'done' | 'current' | 'skipped' | 'partial' | 'partner' | 'ahead' | 'none'
export const choiceOf = (answers: Answer[], id: QuestionId) => answers.find(a => a.question === id)?.choice

// Numeric choices are persisted in drafts; keep their original option order.
const CHOICE = {
  end: { separated: 0, inContact: 1, paused: 2, together: 3 },
  told: { discussed: 0, hinted: 1, unaware: 2, partnerRaised: 3 },
  weigh: { consideringLeaving: 0, dissatisfied: 1, satisfied: 2 },
  discuss: { yes: 0 },
  trying: { yes: 0 },
  now: { past: 0, future: 1, tooFresh: 2 },
} as const

export function nextQuestion(answers: Answer[]): QuestionId | null {
  const get = (id: QuestionId) => choiceOf(answers, id)
  const firstMissing = (ids: QuestionId[]) => ids.find(id => get(id) === undefined) ?? null
  const end = get('end')
  if (end === undefined) return 'end'
  if (end === CHOICE.end.together) {
    const told = get('told')
    if (told === undefined) return 'told'
    if (told === CHOICE.told.hinted || told === CHOICE.told.unaware) {
      if (get('weigh') === undefined) return 'weigh'
      return get('weigh') === CHOICE.weigh.satisfied ? null : firstMissing(['partner'])
    }
    if (get('discuss') === undefined) return 'discuss'
    if (get('discuss') === CHOICE.discuss.yes && get('trying') === undefined) return 'trying'
    return firstMissing(['env', 'partner', 'cycle'])
  }
  if (end === CHOICE.end.paused) return firstMissing(['pauseTalk', 'env', 'partner', 'cycle'])
  if (get('now') === undefined) return 'now'
  if (get('now') === CHOICE.now.future && get('lessons') === undefined) return 'lessons'
  if (get('now') !== CHOICE.now.tooFresh && get('story') === undefined) return 'story'
  if (get('who') === undefined) return 'who'
  if (get('who') === 1 && get('surprise') === undefined) return 'surprise'
  return firstMissing(['talk', 'tried', 'envEnd', 'contact', 'cycle'])
}

function determineStage(answers: Answer[]): number | null {
  const get = (id: QuestionId) => choiceOf(answers, id)
  const end = get('end')
  if (end === CHOICE.end.paused) return 5
  if (end !== CHOICE.end.together) {
    if (get('now') === CHOICE.now.tooFresh) return 6
    if (get('now') === CHOICE.now.past) return 7
    return 8
  }
  const told = get('told')
  if (told === CHOICE.told.hinted || told === CHOICE.told.unaware) {
    if (get('weigh') === CHOICE.weigh.satisfied) return null
    if (get('weigh') === CHOICE.weigh.dissatisfied) return 1
    return 2
  }
  if (get('discuss') !== CHOICE.discuss.yes) return 3
  if (get('trying') === CHOICE.trying.yes) return 5
  return 4
}

export function getResult(answers: Answer[], content: JourneyContent) {
  const get = (id: QuestionId) => choiceOf(answers, id)
  const end = get('end')
  const currentStage = determineStage(answers)
  const ordered = [...content.stages].sort((a, b) => a.no - b.no)
  const stages = ordered.map((stage) => ({
    id: stage.no, name: stage.title,
    status: (!currentStage ? 'none' : stage.no < currentStage ? 'done' : 'ahead') as StageStatus,
  }))
  const set = (id: number, status: StageStatus) => { stages[id - 1].status = status }
  const separated = end === CHOICE.end.separated || end === CHOICE.end.inContact
  const discussion = get(end === CHOICE.end.paused ? 'pauseTalk' : 'talk')
  if (discussion !== undefined) {
    set(3, discussion === 2 ? 'skipped' : 'done')
    set(4, discussion === 0 ? 'done' : 'skipped')
  }
  if (get('told') === CHOICE.told.partnerRaised || (get('who') === 1 && get('surprise') === 0)) {
    set(1, 'partner'); set(2, 'partner')
  } else if (get('who') === 1 && get('surprise') === 1) {
    set(1, 'partial'); set(2, 'partner')
  }
  if (separated) set(5, get('tried') === 0 ? 'done' : 'skipped')
  if (currentStage === 8) set(7, get('story') === 0 ? 'done' : 'skipped')
  if (currentStage) set(currentStage, 'current')

  const insights: string[] = []
  if (discussion === 1) insights.push('Проблему назвали, но переговоров о том, что делать, по сути не было.')
  if (discussion === 2) insights.push('Открытого разговора о проблеме не было. К этой теме можно вернуться, если вам это нужно.')
  if (get('contact') === 2) insights.push('Новые правила общения пока не установлены: непонятно, кто вы друг другу.')
  if (currentStage === 8 && get('story') === 1) insights.push('Вы смотрите вперёд, не сложив версию разрыва. Стадии не обязательно идут по порядку.')
  if (currentStage === 8 && get('lessons') === 2) insights.push('Взгляд уже обращён в будущее, но требования к новым отношениям ещё не сформулированы.')
  const repeatingCycle = get('cycle') === 0
  if (repeatingCycle) insights.push('Вы уже расходились и сходились снова. Поэтому путь может быть не прямым: некоторые этапы могут возвращаться.')
  const partnerChoice = get('partner')
  const partnerText = partnerChoice !== undefined ? [
    'Возможно, партнёр начал внутренний процесс раньше вас.',
    'Похоже, вы сейчас примерно в одной точке процесса.',
    'Возможно, вы начали внутренне принимать решение раньше партнёра.',
    'Сейчас состояние партнёра для вас неясно. Мы не будем пытаться определять его стадию точно.',
  ][partnerChoice] : get('told') === CHOICE.told.partnerRaised || get('who') === 1 ? 'Для партнёра этот процесс мог начаться раньше, чем для вас.' : ''
  const asymmetry = partnerChoice === 0 || partnerChoice === 2 || get('told') === CHOICE.told.partnerRaised || get('who') === 1
  if (!asymmetry && partnerText) insights.push(partnerText)
  // Support is context, never a ninth stage or a separate progress step.
  const supportChoice = get(separated ? 'envEnd' : 'env')
  const support = supportChoice === undefined ? null : (separated ? [
    'Близкие знали о проблемах ещё до расставания.',
    'Близкие узнали только после расставания.',
    'Близкие пока не знают о расставании.',
  ] : [
    'Вы советуетесь с близкими, пока ищете решение в паре.',
    'Близкие знают о проблемах, но вы их с ними не обсуждаете.',
    'Близкие пока не знают о ваших проблемах.',
  ])[supportChoice]
  const stageDoc = currentStage ? stageByNo(content, currentStage) : null
  return {
    currentStage,
    currentStageName: stageDoc?.title ?? 'Стадия не определена',
    internalStageName: stageDoc?.title ?? null,
    description: stageDoc?.user_text ?? content.quiz.undetermined_text,
    stages, asymmetry, partnerText, repeatingCycle, support, insights,
    unfinishedProcesses: stages.filter(s => s.status === 'skipped' || s.status === 'partial'),
  }
}

// The submitted artifacts ride /onboard two ways (#4481): the typed journey
// field {stage, answers} for the track, and tag strings built from the
// content pack's templates for the pre-payment legacy graph.
export interface QuizSubmission {
  journey: { stage: number | null; answers: Record<string, string> }
  tags: string[]
}

export function quizSubmission(answers: Answer[], content: JourneyContent): QuizSubmission {
  const result = getResult(answers, content)
  const byTitle = new Map(content.quiz.questions.map((question) => [question.text, question]))
  const journeyAnswers: Record<string, string> = {}
  const answerTags: string[] = []
  for (const { question, choice } of answers) {
    const match = byTitle.get(questions[question].title)
    const answer = match?.answers[choice]
    if (!match || !answer) continue
    journeyAnswers[match.id] = answer.id
    if (match.pass_to_bot) {
      answerTags.push(renderTemplate(content.quiz.tags.answer, {
        label: match.pass_to_bot,
        answer: answer.text,
      }))
    }
  }
  const stageDoc = result.currentStage ? stageByNo(content, result.currentStage) : null
  const stageTag = stageDoc
    ? renderTemplate(content.quiz.tags.stage, {
        title: stageDoc.title, no: stageDoc.no, total: content.stages.length,
      })
    : content.quiz.tags.stage_none
  return {
    journey: { stage: result.currentStage, answers: journeyAnswers },
    tags: [stageTag, ...answerTags],
  }
}
